import { generateUserAccessListForQuery, PartLibrarySearchFilters, } from "@buildwithflux/models";
/**
 * Returns the AND operator for the given search provider
 */
function getAndOperator(searchProvider) {
    return searchProvider === "typesense" ? "&&" : "AND";
}
/**
 * Returns the OR operator for the given search provider
 */
function getOrOperator(searchProvider) {
    return searchProvider === "typesense" ? "||" : "OR";
}
export class PartLibrarySearchPolicy {
    repositories;
    currentUserUid;
    authContext;
    constructor(repositories, currentUserUid = undefined, authContext = undefined) {
        this.repositories = repositories;
        this.currentUserUid = currentUserUid;
        this.authContext = authContext;
    }
    getUserToken() {
        return this.currentUserUid ?? undefined;
    }
    async getFilters(searchProvider = "algolia") {
        const { anyUsersHaveAccess, isPublic, isArchived } = PartLibrarySearchFilters;
        const andOperator = getAndOperator(searchProvider);
        const orOperator = getOrOperator(searchProvider);
        if (!this.currentUserUid) {
            // Anonymous users can see only public parts
            return `${isPublic(true)} ${andOperator} ${isArchived(false)}`;
        }
        // NOTE: we could potentially run up against the algolia filter limit
        // if the user is a member of many orgs and this is combined with other
        // high-multiplicity filters in the future.
        //
        // The limit is 1000 filters per query as of 2023-10-05.
        // https://www.algolia.com/doc/guides/scaling/algolia-service-limits/#filters-facets-and-rules-limits
        //
        // The "soft" key-length limit is 500 characters as of 2023-11-27.
        // https://www.algolia.com/doc/api-reference/api-methods/generate-secured-api-key/
        const currentUserMemberships = await this.repositories.organizationRepository.getAllDecoratedOrganizationMembersForUserUid(this.currentUserUid);
        const currentUserHasAccess = anyUsersHaveAccess(generateUserAccessListForQuery(this.currentUserUid, currentUserMemberships), searchProvider);
        if (this.authContext?.activeDocumentUid) {
            const document = await this.repositories.documentRepository.getBaseDataWithoutMigrations(this.authContext.activeDocumentUid);
            if (document && document.owner_uid !== this.currentUserUid) {
                const ownerMemberships = await this.repositories.organizationRepository.getPublicOrganizationMembersForUserUid(document.owner_uid);
                const ownerHasAccess = anyUsersHaveAccess(generateUserAccessListForQuery(document.owner_uid, ownerMemberships), searchProvider);
                // We have to add isPublic(true) to each disjunction instead of at the top level due to an Algolia limitation:
                // https://www.algolia.com/doc/api-reference/api-parameters/filters/#how-to-use
                return `(${isPublic(true)} ${orOperator} ${ownerHasAccess}) ${andOperator} (${isPublic(true)} ${orOperator} ${currentUserHasAccess}) ${andOperator} ${isArchived(false)}`;
            }
        }
        // Double negative for isPublic seems weird but was probably intended to match any non-false values.
        const baseIsPublicFilter = searchProvider === "typesense" ? isPublic(true) : `NOT ${isPublic(false)}`;
        const baseFilters = `(${currentUserHasAccess} ${orOperator} ${baseIsPublicFilter}) ${andOperator} ${isArchived(false)}`;
        return baseFilters;
    }
    /**
     * Returns Qdrant-compatible filters for permission-based filtering
     */
    async getQdrantFilters() {
        const baseConditions = [{ key: "metadata.archived", match: { value: false } }];
        if (!this.currentUserUid) {
            // Anonymous users can see only public parts
            return {
                must: [...baseConditions, { key: "metadata.anyoneCanView", match: { value: true } }],
            };
        }
        const currentUserMemberships = await this.repositories.organizationRepository.getAllDecoratedOrganizationMembersForUserUid(this.currentUserUid);
        const userAccessList = generateUserAccessListForQuery(this.currentUserUid, currentUserMemberships);
        const userAccessConditions = userAccessList.map((accessItem) => ({
            key: "metadata.user_access_list",
            match: { value: accessItem },
        }));
        if (this.authContext?.activeDocumentUid) {
            const document = await this.repositories.documentRepository.getBaseDataWithoutMigrations(this.authContext.activeDocumentUid);
            if (document && document.owner_uid !== this.currentUserUid) {
                const ownerMemberships = await this.repositories.organizationRepository.getPublicOrganizationMembersForUserUid(document.owner_uid);
                const ownerAccessList = generateUserAccessListForQuery(document.owner_uid, ownerMemberships);
                const ownerAccessConditions = ownerAccessList.map((accessItem) => ({
                    key: "metadata.user_access_list",
                    match: { value: accessItem },
                }));
                return {
                    must: [
                        ...baseConditions,
                        {
                            should: [
                                { key: "metadata.anyoneCanView", match: { value: true } },
                                { must: [...userAccessConditions, ...ownerAccessConditions] },
                            ],
                        },
                    ],
                };
            }
        }
        return {
            must: [
                ...baseConditions,
                {
                    should: [{ key: "metadata.anyoneCanView", match: { value: true } }, ...userAccessConditions],
                },
            ],
        };
    }
}
//# sourceMappingURL=policies.js.map